<template>
  <v-overlay
    :value="!getAuthenticated"
    opacity="1"
    z-index="9999"
    class="login-overlay"
    :class="{ 'roll-up': isRollingUp }"
  >
    <Header :slug="slug"></Header>
    <div class="login-wrapper white--text mt-4">
      <v-container fluid class="fill-height pa-0">
        <v-row no-gutters class="fill-height">
          <v-col
            cols="12"
            md="6"
            class="welcome-side d-flex flex-column justify-center align-center text-center pa-8"
            :style="gradientColorStyle"
          >
            <div class="welcome-content mt-6">
              <CompanyWelcome
                v-if="appState === 'its-a-company'"
                @scroll-to-login="scrollToLogin"
                :company="company"
              />
              <BitsInvoiceWelcome
                v-else-if="appState === 'its-bitsinvoice'"
                @scroll-to-login="scrollToLogin"
              />
              <LegalBillingWelcome
                v-else-if="appState === 'its-legalbilling'"
                @scroll-to-login="scrollToLogin"
              />
            </div>
          </v-col>

          <v-col
            id="login-section"
            cols="12"
            md="6"
            class="login-side d-flex align-center justify-center pa-6 white"
          >
            <div class="login-form-container full-width-mobile">
              <LoginWP
                v-if="loginWithPassword"
                @login="loginCompleted"
                :slug="slug"
              />
              <Login v-else @login="loginCompleted" :slug="slug" />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-overlay>
</template>

<script>
import axios from "@/plugins/axios";
import companiesJs from "@/plugins/companies";
import helpersJs from "@/plugins/helpers";
import Header from "@/components/Headers/Header.vue";
import Login from "@/modules/Auth/Login.vue";
import LoginWP from "@/modules/Auth/LoginWP.vue";
import BitsInvoiceWelcome from "@/components/General/BitsInvoiceWelcome.vue";
import LegalBillingWelcome from "@/components/General/LegalBillingWelcome.vue";
import CompanyWelcome from "@/components/General/CompanyWelcome.vue";

export default {
  name: "LoginOverlay",
  components: {
    Header,
    Login,
    LoginWP,
    BitsInvoiceWelcome,
    LegalBillingWelcome,
    CompanyWelcome,
  },
  props: {
    slug: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      auth: false,
      appState: "",
      isRollingUp: false,
      loginWithPassword: false,
      company: null,
      logo: null,
    };
  },
  computed: {
    getUserCompany() {
      return (
        this.$store?.getters?.getUserCompany || {
          name: "",
          physical_address: "",
          primary_phone: "",
          city: "",
          email: "",
          currency: "R",
        }
      );
    },
    getAuthenticated() {
      return this.$store.getters.getAuthenticated || false;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor || "#FFF";
    },
    gradientColorStyle() {
      return { background: this.getThemeColor };
    },
  },
  watch: {
    slug(newVal) {
      if (newVal) this.determineAppState();
    },
  },
  async mounted() {
    this.updateScreenWidth();
    window.addEventListener("resize", this.updateScreenWidth);

    await Promise.all([this.determineAppState(), this.getUserLocation()]);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenWidth);
  },
  methods: {
    updateScreenWidth() {
      if (this.$vuetify?.breakpoint) {
        this.$store.commit("setScreenWidth", this.$vuetify.breakpoint.width);
      }
    },
    async determineAppState() {
      const domain = window.location.hostname;

      if (this.slug) {
        const res = await companiesJs.getCompanyBySlug(this.slug);
        if (
          res &&
          res.status === "success" &&
          (await this.setupCompanyData(res.company))
        ) {
          return;
        }
      }

      if (
        domain.includes("legalcasebilling") ||
        domain.includes("bitsinvoice") ||
        domain.includes("172.20.10") ||
        domain.includes("baronitsolutions")
      ) {
        this.loginWithPassword = true;
      } else {
        this.loginWithPassword = false;
      }

      if (domain.includes("legalcasebilling")) {
        this.appState = "its-legalbilling";
      } else if (domain.includes("bitsinvoice")) {
        this.appState = "its-bitsinvoice";
      } else {
        await this.getCompanyWithDomain(domain);
      }
    },
    async setupCompanyData(compData) {
      const company = compData?.company || null;
      if (!company) return false;

      this.company = company;
      this.appState = "its-a-company";
      helpersJs.initialiseOrUpdateCompanyDataInStorage(null, compData);

      if (company.emis) {
        const logos = await companiesJs.getLogos(company.emis);
        if (logos?.length > 0) {
          this.logo = logos[0];
        }
      }
      return true;
    },
    async getCompanyWithDomain(domain) {
      const compData = await companiesJs.getCompanyWithDomain(domain);
      await this.setupCompanyData(compData);
    },
    scrollToLogin() {
      const loginSection = document.getElementById("login-section");
      loginSection?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    loginCompleted() {
      this.isRollingUp = false;
      this.$emit("login-completed");
    },
    async getUserLocation() {
      try {
        //use company data to detect password or otp login

        const locationData = await axios.postData("/get-user-location", {
          foo: "bar",
        });

        const itsForeign =
          locationData?.currencyCode !== "R" &&
          locationData?.currencyCode !== "ZAR"
            ? true
            : false;

        const currency = itsForeign
          ? locationData?.currencyCode
          : this.company?.currency
          ? this.company?.currency
          : "R";

        this.$store.commit("setCurrency", currency);
      } catch (error) {
        console.error("Failed to fetch user location:", error);
      }
    },
  },
};
</script>

<style scoped>
.login-overlay ::v-deep .v-overlay__content {
  width: 100vw;
  height: 100vh;
}

.login-wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #ffffff;
  transition: transform 0.7s cubic-bezier(0.77, 0, 0.175, 1);
}

.login-overlay.roll-up .login-wrapper {
  transform: translateY(-100vh);
}

.welcome-side,
.login-side {
  min-height: 100vh;
}

.login-side {
  color: #333333;
}

.welcome-content {
  max-width: 480px;
}

.full-width-mobile {
  width: 100%;
  max-width: 420px;
}
</style>
